import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  GridTable,
  ConfirmModelDialogBox,
  ErrorValidationBox,
  LoadingBox,
  PrivilegesChecker,
  SearchBoxGeneric,
} from "..";
import { useSearchBoxGeneric, useSearchedAccounts } from "../../hooks";
import { getLabelName, isArabicCurrentLanguage } from "../../utils";
import {
  AccountResponseResultModel,
  ActionButtonsModel,
  ActionTypeEnum,
  AcTransactionSearchModel,
  PageEnum,
  RequestActionModel,
  SearchAccountsSampleRequestModel,
  SearchFieldRequestModel,
  SearchItemRequestModel,
  ToastModel,
  ValidationErrorModel,
} from "../../models";
import {
  deleteAccount,
  searchAccountsWithSampleParameters,
} from "../../serviceBroker/accountApiServiceBroker";

interface AccountListProps {
  parentID: number | null;
  setToastModel: Dispatch<SetStateAction<ToastModel>>;
  setIsAccountLoading: (isLoading: boolean) => void;
  handleDeleteAccount: (id: number) => void;
  fetchAccountInfoByID: (accountID: number | null) => Promise<void>;
  reFetchAccounts: boolean;
}

const AccountsList: React.FC<AccountListProps> = ({
  parentID,
  setToastModel,
  setIsAccountLoading,
  handleDeleteAccount,
  fetchAccountInfoByID,
  reFetchAccounts,
}) => {
  //#region variables
  const defaultPageSize: number = 10;
  const initialSearchFields: SearchFieldRequestModel[] = [
    { key: "accountName", value: "", label: "Name", placeholder: "Enter Name" },
    { key: "accountCode", value: "", label: "Code", placeholder: "Enter Code" },
  ];
  const initialSearchRequest: SearchAccountsSampleRequestModel = {
    pageNumber: 1,
    pageSize: defaultPageSize,
    parentId: null, //parentID,
    accountCode: null,
    accountName: null,
  };
  //#endregion
  //#region state
  const isInitialMount = useRef(true);
  const [searchedAccounts, setSearchedAccounts] = useState<
    AccountResponseResultModel[]
  >([]);
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletedAccountID, setDeletedAccountID] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrorModel[]
  >([]);
  const {
    fields: searchFields,
    onChange: handleSearchFieldChange,
    onSearch: handleSearch,
    onReset: handleResetSearch,
    searchedFields,
  } = useSearchBoxGeneric(initialSearchFields);
  const [searchRequest, setSearchRequest] =
    useState<SearchAccountsSampleRequestModel>(initialSearchRequest);
  const [totalRows, setTotalRows] = useState(0);
  //#endregion
  //#region useEffect
  // useEffect(() => {
  //   setSearchRequest({...initialSearchRequest,parentId: parentID});
  //   //setSearchRequest(prevState =>({...prevState,parentId:parentID}));
  //   setValidationErrors([]);
  // }, [parentID]);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const fillData = async () => {
      setLoading(true);
      await SearchData();
      setLoading(false);
    };
    fillData().then(() => {});
  }, [
    searchRequest.pageNumber,
    searchRequest.pageSize,
    searchRequest.searchGuid,
    searchRequest.accountName,
    searchRequest.accountCode,
    reFetchAccounts,
  ]);
  useEffect(() => {
    if (isInitialMount.current) return;
    const updatedSearchRequest = searchFields.reduce<
      Partial<SearchAccountsSampleRequestModel>
    >((acc, field) => {
      if (field.key === "accountName") acc.accountName = field.value;
      if (field.key === "accountCode") acc.accountCode = field.value;
      return acc;
    }, {});
    //debounceSearchRequestUpdate(updatedSearchRequest);
    setSearchRequest((prev) => ({ ...prev, ...updatedSearchRequest }));
  }, [searchedFields]);
  //#endregion
  //#endregion
  //#region functions
  const SearchData = async () => {
    const result = await searchAccountsWithSampleParameters(searchRequest);
    // const result = await searchAccountsWithSampleParameters({...searchRequest,accountName:searchRequest.accountName==null?null:encodeURIComponent(searchRequest.accountName)});
    setSearchedAccounts(result?.Result || []);
    setTotalRows(result?.TotalRecoredCount || 0);
  };
  const deleteActions: (deletedId: number) => ActionButtonsModel[] = (
    deletedID
  ) => {
    return [
      {
        text: getLabelName("yes"),
        onClick: async () => {
          await handleVoucherDeletion(deletedID);
        },
      },
      {
        text: getLabelName("No"),
        onClick: () => {
          setShowDeleteModel(false);
          setDeletedAccountID(null);
        },
      },
    ];
  };
  const handleEdit = async (id: number | null) => {
    await fetchAccountInfoByID(id);
  };
  const handleVoucherDeletion = async (_id: number) => {
    try {
      setIsAccountLoading(true);
      await deleteAccount(deletedAccountID as number);
      handleDeleteAccount(deletedAccountID as number);
      await SearchData();
      setToastModel({ show: true });
    } catch (err) {
      setValidationErrors(err as ValidationErrorModel[]);
    } finally {
      setShowDeleteModel(false);
      setDeletedAccountID(null);
      setIsAccountLoading(false);
    }
  };
  const onActionEvent = async (o: RequestActionModel) => {
    switch (o.action) {
      case ActionTypeEnum.Update:
        await handleEdit(o.id || o.request.id);
        break;
      case ActionTypeEnum.Delete:
        setShowDeleteModel(true);
        setDeletedAccountID(o.id || o.request.id);
        break;
    }
  };
  const onCurrentPageChange = (pageNumber: number) => {
    setSearchRequest((prev) =>
      pageNumber !== prev.pageNumber ? { ...prev, pageNumber } : prev
    );
  };
  const onPageSizeChange = async (pageSize: number) => {
    setSearchRequest((prev) =>
      pageSize !== prev.pageSize ? { ...prev, pageSize } : prev
    );
  };
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      {<ErrorValidationBox errors={validationErrors} />}
      <PrivilegesChecker formId={PageEnum.AccountsTree} action="EnableSearch">
        <SearchBoxGeneric
          searchFields={searchFields}
          onChangeHandler={handleSearchFieldChange}
          onSearchHandler={handleSearch}
          onResetHandler={handleResetSearch}
        />
      </PrivilegesChecker>
      <div className="row row-cols-1">
        <ConfirmModelDialogBox
          isModelVisible={showDeleteModel}
          onCloseEvent={() => {
            setShowDeleteModel(false);
            setDeletedAccountID(null);
          }}
          actions={deleteActions(deletedAccountID!)}
        >
          <>{getLabelName("Are You Sure You Want Delete ")}</>
        </ConfirmModelDialogBox>
        <GridTable
          data={searchedAccounts}
          columnsProps={{
            actions: [ActionTypeEnum.Update, ActionTypeEnum.Delete],
            isArabic: isArabicCurrentLanguage(),
            onActionEvent,
          }}
          externalLoading={loading}
          totalRows={totalRows}
          currentPage={searchRequest.pageNumber || 1}
          pageSize={searchRequest.pageSize || defaultPageSize}
          onCurrentPageChange={onCurrentPageChange}
          onPageSizeChange={onPageSizeChange}
          paginationType="server"
          formId={PageEnum.AccountsTree}
        />
      </div>
    </>
  );
  //#endregion
};
export default AccountsList;
